import { useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function triggerDownload() {
  const res = await axios.get(`${API}/guide/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "Guida-AI-Instagram.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export const LeadForm = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done

  // Subtle 3D tilt on mouse move
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 150, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 150, damping: 18 });

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const resetMouse = () => { mx.set(0); my.set(0); };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fullName.trim().length < 2) {
      toast.error("Inserisci il tuo nome e cognome.");
      return;
    }
    if (!emailValid) {
      toast.error("Inserisci un indirizzo email valido.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, { full_name: fullName.trim(), email });
      await triggerDownload();
      setStatus("done");
      toast.success("Guida inviata! Il download è iniziato.");
    } catch (err) {
      console.error(err);
      setStatus("idle");
      toast.error("Qualcosa è andato storto. Riprova.");
    }
  };

  return (
    <motion.div
      onMouseMove={handleMouse}
      onMouseLeave={resetMouse}
      style={{ rotateX, rotateY, transformPerspective: 1200 }}
      className="relative rounded-2xl border border-[#27272a] bg-[#121212]/70 backdrop-blur-xl p-8 sm:p-10"
      data-testid="lead-form-card"
    >
      <div className="pointer-events-none absolute -inset-px rounded-2xl border border-[#7c3aed]/20" />
      <span className="text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-[#a78bfa]">
        Download gratuito
      </span>

      {status === "done" ? (
        <div className="mt-8" data-testid="lead-success">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#7c3aed]">
            <Check className="h-7 w-7 text-white" strokeWidth={3} />
          </div>
          <h3 className="mt-6 text-2xl font-extrabold">Perfetto, {fullName.split(" ")[0]}!</h3>
          <p className="mt-3 text-[#a1a1aa] font-light">
            Il download della guida è partito. Se non parte,{" "}
            <button
              onClick={triggerDownload}
              className="text-[#a78bfa] underline underline-offset-4"
              data-testid="redownload-button"
            >
              clicca qui
            </button>.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-2" data-testid="lead-form">
          <div className="field">
            <input
              id="name"
              type="text"
              placeholder=" "
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              data-testid="name-input"
            />
            <label htmlFor="name">Nome e cognome</label>
          </div>
          <div className="field pt-4">
            <input
              id="email"
              type="email"
              placeholder=" "
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="email-input"
            />
            <label htmlFor="email">Indirizzo email</label>
          </div>

          <button
            type="submit"
            disabled={status === "loading"}
            data-testid="download-guide-button"
            className="group mt-8 flex w-full items-center justify-between gap-4 rounded-full bg-[#7c3aed] px-7 py-4 text-left text-base font-bold text-white transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#8b4bf0] hover:shadow-[0_18px_45px_-12px_rgba(124,58,237,0.7)] disabled:opacity-70"
          >
            {status === "loading" ? (
              <>
                Invio in corso
                <Loader2 className="h-5 w-5 animate-spin" />
              </>
            ) : (
              <>
                Scarica gratis la guida
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition-transform duration-300 group-hover:translate-y-1">
                  <ArrowDown className="h-5 w-5" />
                </span>
              </>
            )}
          </button>
          <p className="pt-4 text-xs font-light text-[#71717a]">
            Nessuno spam. Ti inviamo solo la guida in formato PDF.
          </p>
        </form>
      )}
    </motion.div>
  );
};
